import moment from 'moment';
import { combineLatest, EMPTY, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { ChangeDetectorRef, DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { DynamicTranslationService, FundService } from '@gs-2.0/core';

import { CONTRIBUTION_TYPE, INPUT_TYPE } from '../../../_core/constants/Contribute';
import { COUNTRY_NAMES } from '../../../_core/constants/countries';
import FundTypes from '../../../_core/constants/fund-types';
import PageTitles from '../../../_core/constants/page-titles';
import { CustomValidators } from '../../../_core/helpers/custom-validators.helpers';
import SharedMethodsHelpers from '../../../_core/helpers/shared-methods.helpers';
import { Charity } from '../../../_core/models/charity.model';
import { CurrencySet, SearchCharity } from '../../../_core/models/contributeData';
import { CustomControl } from '../../../_core/models/CustomForms.model';
import { Fund } from '../../../_core/models/fund';
import { TitleService } from '../../../_core/services/title.service';
import { FundNameFormatPipe } from '../../../_shared/pipes/fund-name-format.pipe';
import { RECURRENCE_PATTERN } from '../../../_shared/recurring-form/recurring-form.models';
import { createRangeEndValidator } from '../../../_shared/recurring-form/recurring-form.validator';
import { DonateAsyncService } from './donate-form.async-service';
import { ContributionTypes, TaxDeductionValues } from './donate-form.const';
import {
  AnonymityOption,
  CheckContributionPayload,
  ContributionConstData,
  ContributionDynamicData,
  CurrencyCheck,
  CustomLinkFund,
  NetworkMember,
  RepopulateData,
  SearchCountry,
  TokenData,
} from './donate-form.model';

@Injectable()
export class DonateFormManager {
  private destroyRef = inject(DestroyRef);
  donateForm: UntypedFormGroup;
  constData: ContributionConstData;
  dynamicData: ContributionDynamicData;
  defaultMax = 999999.99;
  organizationName: string;
  donationSource: string | null = null;
  signature: string;
  data: string;
  isSignatureValid: boolean = false;
  isDAFOwner: boolean;
  loggedInFundName: string | undefined;
  loggedInFundNumber: string | undefined;
  isSwitzerland = false;
  isSag = false;
  disableAnonymous = false;
  showCharityValidityError = false;
  fundTypes = FundTypes;
  isBrazil = false;
  // Custom contribution-purpose label from the country's contriConfig (null when not configured).
  contributionPurposeConfigLabel: string | null = null;
  private defaultContributionPurposeValidator: ValidatorFn[] = [];
  private isSyncingFromAmountCurrency = false;
  private checkMinimumAmount$ = new Subject<void>();

  // prettier ignore
  constructor(
    private fundService: FundService,
    private donateAsyncService: DonateAsyncService,
    private changeDetector: ChangeDetectorRef,
    private translateService: DynamicTranslationService,
    private fundNameFormatPipe: FundNameFormatPipe,
    private titleService: TitleService
  ) {
    this.isDAFOwner =
      this.fundService.selectedFund()?.fundType === FundTypes.DAF &&
      this.fundService.selectedFund()?.contactRole?.includes('Fund Holder 1');
    this.loggedInFundName = this.fundService.selectedFund()?.fundName;
    this.loggedInFundNumber = this.fundService.selectedFund()?.fundNumber;
  }

  initForm(
    constData: ContributionConstData,
    dynamicData: ContributionDynamicData,
    isRecurring = false,
    allowAnonymousToOwnDAF = false,
    isDAFOwner = false
  ): UntypedFormGroup {
    this.donateForm = new UntypedFormGroup({
      countryAndCharityInfo: new UntypedFormGroup({
        countryDonatingFrom: new CustomControl({
          label: 'DONATE_CONTRIBUTION_LABELS_COUNTRY',
          placeholder: 'GENERAL_TYPE_TO_SEARCH',
          validation: [['required', 'FORM_VALIDATORS_DONATION_COUNTRY_REQUIRED']],
          hasAuxControl: true,
          type: INPUT_TYPE.AUTOCOMPLETE,
        }),
        taxDeduction: new CustomControl({
          label: 'DONATE_CONTRIBUTION_LABELS_TAX_DEDUCTION',
          placeholder: 'GENERAL_SELECT_OPTION',
          helperText: 'DONATE_CONTRIBUTION_GENERAL_TAX_DEDUCTION_INFO',
          validation: [['required', 'FORM_VALIDATORS_TAX_DEDUCTION_REQUIRED']],
          type: INPUT_TYPE.DROPDOWN,
        }),
        taxDeductionFund: new CustomControl({
          label: 'contribute.section2.charity.label',
          type: INPUT_TYPE.TEXT,
          value: '',
        }),
        specificCharity: new CustomControl({
          label: 'contribute.section2.charity.label',
          type: INPUT_TYPE.TEXT,
          value: '',
        }),
        charity: new CustomControl({
          label: this.isSwitzerlandWithCode
            ? 'contribute.section2.original_donor_recommendation'
            : 'contribute.section2.charity.label',
          placeholder: 'GENERAL_TYPE_TO_SEARCH',
          hasAuxControl: true,
          type: INPUT_TYPE.AUTOCOMPLETE,
        }),
      }),
      contributionTypeInfo: new UntypedFormGroup({
        contributionType: new CustomControl({
          value: '',
          label: 'DONATE_CONTRIBUTION_LABELS_CONTRIBUTION_TYPE',
          placeholder: 'GENERAL_SELECT_OPTION',
          validation: [[Validators.required, 'FORM_VALIDATORS_CONTRIBUTION_TYPE_REQUIRED']],
          type: INPUT_TYPE.DROPDOWN,
        }),
        assetDescription: new CustomControl({
          label: 'DONATE_CONTRIBUTION_LABELS_ASSET_DESCRIPTION',
          placeholder: 'DONATE_CONTRIBUTION_LABELS_ASSET_DESCRIPTION',
          validation: [['required', 'FORM_VALIDATORS_ASSET_DESCRIPTION_REQUIRED']],
          maxLength: 280,
          visible: false,
          type: INPUT_TYPE.TEXTAREA,
        }),
        dafProvider: new CustomControl({
          label: 'DONATE_CONTRIBUTION_LABELS_DAF_PROVIDER',
          placeholder: 'DONATE_CONTRIBUTION_LABELS_DAF_PROVIDER',
          validation: [['required', 'FORM_VALIDATORS_DAF_PROVIDER_REQUIRED']],
          maxLength: 280,
          visible: false,
          type: INPUT_TYPE.TEXT,
        }),
        fundName: new CustomControl({
          label: 'DONATE_CONTRIBUTION_LABELS_FUND_NAME',
          placeholder: 'DONATE_CONTRIBUTION_LABELS_FUND_NAME',
          validation: [['required', 'FORM_VALIDATORS_FUND_NAME_REQUIRED']],
          maxLength: 280,
          visible: false,
          type: INPUT_TYPE.TEXT,
        }),
      }),
      financialInfo: new UntypedFormGroup({
        amountCurrency: new CustomControl({
          label: 'contribute.section4.amount.label',
          validation: [[CustomValidators.required, 'FORM_VALIDATORS_AMOUNT_CURRENCY_REQUIRED']],
          value: { amount: null, currency: null },
        }),
        amount: new CustomControl({
          label: 'contribute.section4.amount.label',
          validation: [
            [CustomValidators.required, 'FORM_VALIDATORS_AMOUNT_REQUIRED'],
            [CustomValidators.positiveNumber, 'FORM_VALIDATORS_AMOUNT_POSITIVE'],
            ['maxValue', 'FORM_VALIDATORS_AMOUNT_MAX_EXCEEDED'],
            [CustomValidators.noDecimals, 'FORM_VALIDATORS_AMOUNT_NO_DECIMALS_ALLOWED'],
          ],
          max: 999999.99,
          type: INPUT_TYPE.CURRENCY,
        }),
        currency: new CustomControl({
          placeholder: 'GENERAL_CURRENCY_SHORT',
          validation: [[Validators.required, 'FORM_VALIDATORS_CURRENCY_REQUIRED']],
          hasAuxControl: true,
          type: INPUT_TYPE.AUTOCOMPLETE,
        }),
        anonymousDonation: new CustomControl({
          label: 'DONATE_CONTRIBUTION_LABELS_ANONYMOUS',
          value:
            !this.constData?.isPublic && this.isDAFOwner
              ? allowAnonymousToOwnDAF
                ? constData.defaultAnonymousDonationValue || false
                : false
              : constData.defaultAnonymousDonationValue || false,
        }),
        contributionPurpose: new CustomControl({
          label: 'contribute.section4.purpose.label',
          placeholder: constData.purposePlaceholderText,
          validation:
            !this.constData?.isPublic && this.isDAFOwner && allowAnonymousToOwnDAF
              ? [[CustomValidators.maxLength(250), 'FORM_VALIDATORS_CONTRIBUTION_PURPOSE_MAX']]
              : [
                  [Validators.required, 'FORM_VALIDATORS_CONTRIBUTION_PURPOSE_REQUIRED'],
                  [CustomValidators.maxLength(250), 'FORM_VALIDATORS_CONTRIBUTION_PURPOSE_MAX'],
                ],
          maxLength: 250,
          type: INPUT_TYPE.TEXT,
        }),
      }),
      giverInfo: new UntypedFormGroup({
        isCompany: new CustomControl({
          value: false,
        }),
        companyName: new CustomControl({
          label: 'DONATE_CONTRIBUTION_LABELS_COMPANY_NAME',
          validation: [
            [Validators.required, 'FORM_VALIDATORS_COMPANY_REQUIRED'],
            [CustomValidators.maxLength(74), 'FORM_VALIDATORS_COMPANY_MAX'],
          ],
        }),
        firstName: new CustomControl({
          label: 'DONATE_CONTRIBUTION_LABELS_FIRST_NAME',
          validation: [
            [Validators.required, 'FORM_VALIDATORS_FIRST_NAME_REQUIRED'],
            [CustomValidators.lettersCharacters, 'FORM_VALIDATORS_FIRST_NAME_CHARACTERS'],
            [CustomValidators.minLength(2), 'FORM_VALIDATORS_FIRST_NAME_MIN'],
            [CustomValidators.maxLength(30), 'FORM_VALIDATORS_FIRST_NAME_MAX'],
          ],
        }),
        middleName: new CustomControl({
          label: 'DONATE_CONTRIBUTION_LABELS_MIDDLE_NAME',
          validation: [[CustomValidators.maxLength(30), 'FORM_VALIDATORS_FIRST_NAME_MAX']],
        }),
        lastName: new CustomControl({
          label: 'DONATE_CONTRIBUTION_LABELS_LAST_NAME',
          validation: [
            [Validators.required, 'FORM_VALIDATORS_LAST_NAME_REQUIRED'],
            [CustomValidators.lettersCharacters, 'FORM_VALIDATORS_LAST_NAME_CHARACTERS'],
            [CustomValidators.minLength(2), 'FORM_VALIDATORS_LAST_NAME_MIN'],
            [CustomValidators.maxLength(30), 'FORM_VALIDATORS_LAST_NAME_MAX'],
          ],
        }),
        email: new CustomControl({
          label: 'GENERAL_EMAIL',
          validation: [
            [CustomValidators.required, 'FORM_VALIDATORS_EMAIL_REQUIRED'],
            [CustomValidators.email, 'FORM_VALIDATORS_EMAIL_INVALID'],
          ],
        }),
        country: new CustomControl({
          label: 'DONATE_CONTRIBUTION_LABELS_COUNTRY',
          placeholder: 'GENERAL_SELECT_OPTION',
          validation: [[Validators.required, 'FORM_VALIDATORS_COUNTRY_REQUIRED']],
          type: INPUT_TYPE.DROPDOWN,
        }),
        city: new CustomControl({
          label: 'DONATE_CONTRIBUTION_LABELS_CITY',
          validation: [
            [Validators.required, 'FORM_VALIDATORS_CITY_REQUIRED'],
            [CustomValidators.numbersCharacters, 'FORM_VALIDATORS_CITY_CHARACTERS'],
          ],
        }),
        streetAddress: new CustomControl({
          label: 'DONATE_CONTRIBUTION_LABELS_STREET',
          validation: [
            [Validators.required, 'FORM_VALIDATORS_STREET_REQUIRED'],
            [CustomValidators.lettersNumbersCharacters, 'FORM_VALIDATORS_STREET_CHARACTERS'],
            [CustomValidators.minLength(2), 'FORM_VALIDATORS_STREET_MIN'],
            [CustomValidators.maxLength(150), 'FORM_VALIDATORS_STREET_MAX'],
          ],
        }),
        stateProvince: new CustomControl({
          label: 'DONATE_CONTRIBUTION_LABELS_STATE',
          validation: [
            ['required', 'FORM_VALIDATORS_STATE_REQUIRED'],
            ['lettersNumbersCharacters', 'FORM_VALIDATORS_PROVINCE_CHARACTERS'],
            ['minLength', 'FORM_VALIDATORS_PROVINCE_MIN'],
            ['maxLength', 'FORM_VALIDATORS_PROVINCE_MAX'],
          ],
          type: INPUT_TYPE.TEXT,
        }),
        postalCode: new CustomControl({
          label: 'DONATE_CONTRIBUTION_LABELS_POSTAL_CODE',
          validation: [
            [Validators.required, 'FORM_VALIDATORS_POSTAL_CODE_REQUIRED'],
            [CustomValidators.specialChars, 'FORM_VALIDATORS_POSTAL_CODE_CHARACTERS'],
            [CustomValidators.minLength(2), 'FORM_VALIDATORS_POSTAL_CODE_MIN'],
            [CustomValidators.maxLength(10), 'FORM_VALIDATORS_POSTAL_CODE_MAX'],
          ],
        }),
        shareDonorDetails: new CustomControl({
          value: null,
          validation: [[Validators.required, 'DONATE_CONTRIBUTION_ANONYMITY_REQUIRED']],
        }),
        privacyConsent: new CustomControl({
          value: false,
        }),
      }),
    });
    if (isRecurring) this.addRecurringControls();
    this.constData = constData;
    this.dynamicData = dynamicData;
    if (this.constData.isSwiss) {
      this.countryDonatingFrom.setValidators([Validators.required]);
    } else if (!this.constData.wasRedirected) {
      this.setAndDisableCurrency();
    }
    if (this.constData.isTol) this.taxDeduction.setValidators([Validators.required]);
    this.setCharity();
    this.setPurpose(allowAnonymousToOwnDAF);
    this.setFormListeners();
    this.amount.addValidators([CustomValidators.maxValue(this.amount.max)]);
    this.setCompanyNameValidity();
    if (!this.constData?.isPublic && this.isDAFOwner) this.disableAnonymous = !allowAnonymousToOwnDAF;
    !this.constData.isSwiss && this.shareDonorDetails.setValue(AnonymityOption.NAME);
    return this.donateForm;
  }

  // Add recurring controls
  addRecurringControls(): void {
    if (this.dynamicData) {
      this.constData.isPublic
        ? (this.dynamicData.isPublicRecurring = true)
        : (this.dynamicData.isRecurring = true);
    }
    // Add recurrence form
    this.financialInfo.addControl(
      'recurrenceForm',
      new UntypedFormGroup({
        recurrencePattern: new CustomControl({
          value: RECURRENCE_PATTERN.WEEKLY,
        }),
        recurrenceRange: new UntypedFormGroup(
          {
            rangeOption: new CustomControl({
              value: null,
            }),
            startDate: new CustomControl({
              value: moment(new Date()).valueOf(),
            }),
            endDate: new CustomControl({
              value: null,
            }),
            rangeOccurrences: new CustomControl({
              value: 2,
            }),
          },
          { validators: [createRangeEndValidator()] }
        ),
      })
    );
  }

  removeRecurringControls(): void {
    if (this.dynamicData) {
      this.constData.isPublic
        ? (this.dynamicData.isPublicRecurring = false)
        : (this.dynamicData.isRecurring = false);
    }
    this.financialInfo.removeControl('recurrenceForm');
  }

  setShareDonorDetails(
    defaultAnonymousDonationValue: boolean,
    allowAnonymousToOwnDAF: boolean
  ): AnonymityOption {
    this.disableAnonymous = !allowAnonymousToOwnDAF;
    if (defaultAnonymousDonationValue) {
      return allowAnonymousToOwnDAF ? AnonymityOption.ANONYMOUS : AnonymityOption.NAME;
    } else {
      return AnonymityOption.NAME;
    }
  }
  private validateCharity(charity: Charity): boolean {
    if (!charity) {
      return false;
    }
    if (!this.constData.isPublic && this.loggedInFundType !== FundTypes.CHARITY) {
      return true;
    }
    const isVendorFund = charity.fundType === FundTypes.CHARITY;
    const isSwitzerlandDonation =
      this.countryDonatingFrom.value?.name === COUNTRY_NAMES.SWITZERLAND;
    const isTaxDed = this.taxDeductionValue;
    const isValidSwissDonation = isSwitzerlandDonation && this.constData.isSwiss && isTaxDed;
    return !(isValidSwissDonation && isVendorFund);
  }
  // Form Listeners
  setFormListeners(): void {
    // Debounce and deduplicate minimum amount API calls using switchMap
    this.checkMinimumAmount$
      .pipe(
        debounceTime(300),
        switchMap(() => {
          if (!this.amount?.value || !this.currency?.value?.symbol) {
            this.amount.loading = false;
            return EMPTY;
          }
          this.amount.loading = true;
          return this.donateAsyncService
            .getMinimumAmountForCurrency(this.amount, this.currency.value.symbol)
            .pipe(
              tap((res: CurrencyCheck) => {
                const existingErrors = this.amount.errors;
                this.resetAmountErrors();

                if (res.zeroDecimalCurrency && !Number.isInteger(this.amount.value)) {
                  this.amount.setErrors({ noDecimals: true });
                } else if (this.amount.value < parseFloat(res.value)) {
                  this.setMinimumAmountError(res.value);
                }

                this.handleTaxDeductionMinimum(res, existingErrors);
                this.amount.loading = false;
              }),
              catchError((error) => {
                console.error('Error fetching minimum amount for currency:', error);
                this.amount.loading = false;
                return EMPTY;
              })
            );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    // Sync combined control to separate controls (backward compatibility)
    this.amountCurrency.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value) {
          this.isSyncingFromAmountCurrency = true;

          const previousAmount = this.amount.value;
          const previousCurrency = this.currency.value;

          this.amount.setValue(value.amount, { emitEvent: false });
          this.currency.setValue(value.currency, { emitEvent: false });

          // Manually trigger validations since we suppressed events
          // Check if currency changed (comparing by symbol to handle object references)
          const currencyChanged = previousCurrency?.symbol !== value.currency?.symbol;
          // Check if amount changed
          const amountChanged = previousAmount !== value.amount;

          if (currencyChanged) {
            // Currency changed: set loading and clear old minimum before checking new minimum
            this.amount.loading = true;
            this.amount.helperData.minAmount = null;
            this.checkMinimumAmount();
            this.setAmountMax();
          } else if (amountChanged) {
            // Only amount changed: run checkMinimumAmount (no need for setAmountMax)
            this.checkMinimumAmount();
          }

          this.isSyncingFromAmountCurrency = false;
        }
      });

    // Sync separate controls to combined control
    combineLatest([
      this.amount.valueChanges.pipe(startWith(this.amount.value)),
      this.currency.valueChanges.pipe(startWith(this.currency.value)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([amount, currency]) => {
        // Skip syncing back to amountCurrency if we're currently syncing FROM amountCurrency
        // This prevents circular updates that clear the currency value
        if (this.isSyncingFromAmountCurrency) return;

        this.amountCurrency.setValue({ amount, currency }, { emitEvent: false });
      });

    this.countryDonatingFrom.valueChanges.subscribe((value) => {
      this.showCharityValidityError = false;
      if (!value && this.dynamicData.displayBankDetails) {
        this.dynamicData.displayBankDetails = false;
      }
      this.applyContributionPurposeConfig(value?.networkMember?.contriConfig);
      this.clearServerError();
    });

    this.taxDeduction.valueChanges.subscribe((value) => {
      this.constData.isPublic &&
        this.charityValue &&
        !this.constData.customLink &&
        !this.constData.wasRedirected &&
        this.charity.setValue(null);
      if (value && this.dynamicData?.displayBankDetails) {
        this.dynamicData.displayBankDetails = false;
      }
      this.clearServerError();
      if (!value) return;
      if (!this.constData.isPublic || this.charity.value)
        this.checkDonation({ networkMemberInfo: true });
      if (this.constData.isSwiss) {
        this.setTaxDeductionDetails();
        // Check if tax deduction is selected and taxDeductibleContributionTypes is available
        const taxDeductibleTypes =
          this.countryDonatingFrom.value?.networkMember?.contriConfig
            ?.taxDeductibleContributionTypes;
        const nonTaxDeductibleTypes =
          this.countryDonatingFrom.value?.networkMember?.contriConfig
            ?.nonTaxDeductibleContributionTypes;

        if (value.code && taxDeductibleTypes?.length > 0) {
          // Tax deduction selected: use taxDeductibleContributionTypes
          this.dynamicData.setContributionTypeOptionsFromApi(taxDeductibleTypes);
        } else if (
          !value.code &&
          nonTaxDeductibleTypes?.length > 0 &&
          this.countryName !== COUNTRY_NAMES.UNITED_KINGDOM
        ) {
          // No tax deduction: use nonTaxDeductibleContributionTypes
          this.dynamicData.setContributionTypeOptionsFromApi(nonTaxDeductibleTypes);
        } else {
          // Fallback to current behavior (also handles UK with no tax deduction)
          this.dynamicData.setContributionTypeOptions(this.countryName, value.code);
        }
        this.setTaxDedCurrencies(value.value === 'Yes');
        this.setCurrency();
      }
      this.contributionType.setValue('');
      if (this.constData.isTol) {
        const contributionTypesFromApi =
          this.countryDonatingFrom.value?.networkMember?.contriConfig
            ?.nonTaxDeductibleContributionTypes;
        if (contributionTypesFromApi?.length > 0) {
          this.dynamicData.setContributionTypeOptionsFromApi(contributionTypesFromApi);
        } else {
          this.dynamicData.setContributionTypeOptions(COUNTRY_NAMES.SOUTH_AFRICA, value.code);
        }
        if (value.code) this.contributionType.setValue(this.dynamicData.contributionTypeOptions[0]);
      }
      this.constData.isPublic && this.recurrenceForm && this.removeRecurringControls();
      this.charity.label =
        this.isSwitzerlandWithCode && this.shouldShowOriginalDonorRecommendation
          ? 'contribute.section2.original_donor_recommendation'
          : 'contribute.section2.charity.label';
      if (this.isSwitzerlandWithCode && this.shouldShowOriginalDonorRecommendation) {
        this.showCharityValidityError = this.constData.isPublic
          ? this.constData.customLink
            ? ![FundTypes.CHARITY, FundTypes.PROJECT].includes(
                this.charityValue.fundType as FundTypes
              )
            : this.validateCharity(this.charity.value)
          : ![FundTypes.CHARITY, FundTypes.PROJECT].includes(this.loggedInFundType as FundTypes);

        const currentFund = this.fundService.selectedFund();
        this.specificCharity.patchValue(
          this.constData.isPublic
            ? this.countryDonatingFrom.value?.networkMember?.taxDeductionFundName
            : `${currentFund?.fundName} (#${currentFund?.fundNumber})`
        );
        this.specificCharity.label = this.constData.isPublic
          ? 'contribute.section2.charity.label'
          : 'contribute.section2.original_donor_recommendation';
        this.taxDeductionFund.setValue(
          this.countryDonatingFrom.value?.networkMember?.taxDeductionFundName
        );
        if (this.constData.isPublic && this.constData.customLink) {
          this.charity.clearValidators();
          this.titleService.titleChanged(
            PageTitles.GIVING_TO,
            this.fundNameFormatPipe.transform(
              this.countryDonatingFrom.value?.networkMember?.taxDeductionFundName
            )
          );
        }
        this.taxDeductionFund.addValidators([Validators.required]);
        this.specificCharity.addValidators([Validators.required]);
        !this.constData.isPublic && this.specificCharity.clearValidators();
        this.specificCharity.disable();
        this.taxDeductionFund.disable();
      } else {
        this.showCharityValidityError = false;
      }
    });

    this.contributionType.valueChanges.subscribe((value) => {
      this.clearServerError();

      // Handle empty/null value - hide all conditional fields
      if (!value || value === '') {
        this.hideAllContributionTypeDependentFields();
        return;
      }

      const code = typeof value === 'string' ? value : value.code;

      if (this.constData.isPublic) {
        code !== CONTRIBUTION_TYPE.CREDIT_CARD &&
          this.recurrenceForm &&
          this.removeRecurringControls();
        code === CONTRIBUTION_TYPE.CREDIT_CARD &&
          this.dynamicData.isPublicRecurring &&
          this.addRecurringControls();
      }

      this.dynamicData.setContributionTypeDependentData(
        code,
        this.constData,
        this.taxDeductionValue,
        this.countryDonatingFrom.value?.name
      );
      if (this.constData.isSwiss) {
        this.setAvailableCurrencies();
        this.setCurrency();
      }
      this.setDafFields(code);
      this.setAssetDescription(code);
      this.setAmountMax();
      this.changeDetector.detectChanges();
      if (this.constData.isGlf) this.stopFlowFor(CONTRIBUTION_TYPE.PUBLICLY_TRADED_SECURITIES);
    });

    this.amount.valueChanges
      .pipe(
        debounceTime(300),
        tap(() => this.checkMinimumAmount()),
        map(
          () =>
            this.isSwitzerland &&
            this.amount.value >= 10000 &&
            this.currency.value?.symbol === 'CHF' &&
            this.taxDeduction.value?.code
        ),
        distinctUntilChanged()
      )
      .subscribe((isSag) => {
        this.isSag = isSag;
      });

    this.currency.valueChanges.subscribe(() => {
      this.checkMinimumAmount();
      this.setAmountMax();
    });

    this.country.valueChanges.subscribe(() => {
      if (this.country.enabled) this.setStateProvinceValidity();
    });

    this.isCompany.valueChanges.subscribe(() => {
      this.setCompanyNameValidity();
    });

    this.charity.valueChanges.subscribe((charity) => {
      // Only call checkDonation if charity has fundNumber (complete object)
      charity && charity.fundNumber && this.checkDonation();
      this.clearServerError();
    });

    this.shareDonorDetails.valueChanges.subscribe((value) => {
      this.anonymousDonation.setValue(value === AnonymityOption.ANONYMOUS);
      this.privacyConsent.setValue(false); // Always uncheck when switching between NAME and NAME_EMAIL
      if (value === null || value === AnonymityOption.ANONYMOUS) {
        this.privacyConsent.clearValidators();
      } else {
        if (this.constData.isSwiss) {
          this.privacyConsent.setValidators([Validators.requiredTrue]);
        } else {
          this.privacyConsent.clearValidators();
        }
      }
      this.privacyConsent.updateValueAndValidity();
    });
  }

  // Setters
  setCharity(): void {
    if (this.constData.isPublic) this.charity.setValidators([Validators.required]);
    if (this.constData.isApplicationAssessment) {
      this.donateAsyncService
        .getApplicationAssesmentFund()
        .subscribe((response: CustomLinkFund) => {
          this.charity.patchValue(new Charity(response));
          this.charity.disable();
        });
    } else if (!this.constData.isPublic) {
      const currentFund = this.fundService.selectedFund();
      if (currentFund) {
        this.charity.patchValue(currentFund, { emitEvent: false });
      }
    }
  }

  setTaxDeduction(): void {
    const countryName = this.countryDonatingFrom.value.name;
    const isAutoTaxDeductible = [COUNTRY_NAMES.UNITED_STATES].includes(
      countryName
    );
    const doesNotHaveTaxDeduction = [
      COUNTRY_NAMES.UNITED_KINGDOM,
      COUNTRY_NAMES.AUSTRALIA,
      COUNTRY_NAMES.NEW_ZEALAND,
    ].includes(countryName);

    if (isAutoTaxDeductible) {
      this.taxDeduction.patchValue(TaxDeductionValues[0]);
      this.dynamicData.displayTaxDeduction = false;
    } else if (this.constData.isApplicationAssessment && doesNotHaveTaxDeduction) {
      this.taxDeduction.patchValue({ code: false });
      this.dynamicData.displayTaxDeduction = false;
    } else {
      // Use networkMember.contriConfig.taxDeductibleContributionTypes if available
      const taxDeductibleTypes =
        this.countryDonatingFrom.value?.networkMember?.contriConfig?.taxDeductibleContributionTypes;
      if (taxDeductibleTypes?.length > 0) {
        this.dynamicData.displayTaxDeduction = true;
      } else {
        // Fallback to current behavior if no value or empty array
        this.dynamicData.displayTaxDeduction = this.countryDonatingFrom.value.taxDeduction;
        // Auto-set to No for countries without tax deduction (hidden dropdown)
        if (!this.countryDonatingFrom.value.taxDeduction) {
          this.taxDeduction.patchValue(TaxDeductionValues[1], { emitEvent: false });
        }
      }
    }
  }

  setAvailableCurrencies(): void {
    if (!this.countryDonatingFrom.value || !this.contributionType.value) return;
    const contributionTypeCode =
      typeof this.contributionType.value === 'string'
        ? this.contributionType.value
        : this.contributionType.value.code;
    const allStripeCurrencies = (currency: CurrencySet) =>
      currency.contributionType === CONTRIBUTION_TYPE.CREDIT_CARD;
    const swissMainCurrencies = (currency: CurrencySet) =>
      currency.contributionType !== CONTRIBUTION_TYPE.CREDIT_CARD;
    const taxDeductionCurrenciesCC = (currency: CurrencySet) =>
      currency.taxDeduction === (this.taxDeduction.value.code ? 'TRUE' : 'FALSE') &&
      currency.contributionType === contributionTypeCode;
    const taxDeductionCurrenciesBT = (currency: CurrencySet) =>
      currency.taxDeduction === (this.taxDeduction.value.code ? 'TRUE' : 'FALSE') &&
      currency.contributionType === CONTRIBUTION_TYPE.BANK_TRANSFER;
    switch (contributionTypeCode) {
      case CONTRIBUTION_TYPE.CREDIT_CARD:
        if (this.isBrazil) {
          this.dynamicData.availableCurrencies =
            this.countryDonatingFrom.value.currencySet.filter(taxDeductionCurrenciesCC);
        } else {
          this.dynamicData.availableCurrencies =
            this.countryDonatingFrom.value.currencySet.filter(allStripeCurrencies);
        }
        break;
      case CONTRIBUTION_TYPE.BANK_TRANSFER_OFFLINE:
        if (this.isBrazil) {
          this.dynamicData.availableCurrencies =
            this.countryDonatingFrom.value.currencySet.filter(taxDeductionCurrenciesBT);
        } else {
          this.dynamicData.availableCurrencies =
            this.countryDonatingFrom.value.currencySet.filter(swissMainCurrencies);
        }
        break;
      case CONTRIBUTION_TYPE.OTHER_ASSETS:
      case CONTRIBUTION_TYPE.BANK_TRANSFER_ONLINE:
      case CONTRIBUTION_TYPE.DONOR_ADVISED_FUND:
      case CONTRIBUTION_TYPE.PHYSICAL_CHECK:
      case CONTRIBUTION_TYPE.PUBLICLY_TRADED_SECURITIES:
        this.dynamicData.availableCurrencies =
          this.countryDonatingFrom.value.currencySet.filter(swissMainCurrencies);
        break;
    }
    this.setTaxDedCurrencies(this.taxDeduction.value.code);
    if (!this.currency.value) return;
    if (
      !this.dynamicData.availableCurrencies.find(
        (currency) => currency.symbol === this.currency.value.symbol
      )
    )
      this.currency.setValue(null);
  }

  setTaxDedCurrencies(taxDeduction: boolean): void {
    const hasTaxDeduction = this.countryDonatingFrom?.value?.taxDeduction;
    const taxDeductionCurrencies = (currency: CurrencySet) =>
      currency.taxDeduction === (taxDeduction ? 'TRUE' : 'FALSE');

    if (hasTaxDeduction) {
      this.dynamicData.availableCurrencies =
        this.dynamicData.availableCurrencies.filter(taxDeductionCurrencies);
    }
  }

  private hideAllContributionTypeDependentFields(): void {
    // Hide and clear DAF fields
    this.dafProvider.visible = false;
    this.dafProvider.clearValidators();
    this.dafProvider.patchValue(null);
    this.dafProvider.updateValueAndValidity();

    this.fundName.visible = false;
    this.fundName.clearValidators();
    this.fundName.patchValue(null);
    this.fundName.updateValueAndValidity();

    // Hide and clear asset description
    this.assetDescription.visible = false;
    this.assetDescription.clearValidators();
    this.assetDescription.patchValue(null);
    this.assetDescription.updateValueAndValidity();

    // Clear contribution details text to hide info box
    if (this.dynamicData) {
      this.dynamicData.contributionDetailsText = null;
    }

    this.amount.label = 'contribute.section4.amount.label'; // Reset to default label

    // Remove recurring controls if present
    if (this.recurrenceForm) {
      this.removeRecurringControls();
    }

    // Reset bank details display
    if (this.dynamicData) {
      this.dynamicData.displayBankDetails = false;
    }

    // Reset amount validation to default
    this.amount.setErrors(null);
    this.amount.clearValidators();
    this.amount.max = this.defaultMax;
    this.amount.setValidators([
      CustomValidators.required,
      CustomValidators.positiveNumber,
      CustomValidators.maxValue(this.amount.max),
    ]);
    this.amount.updateValueAndValidity();
  }

  setAssetDescription(contributionType: CONTRIBUTION_TYPE): void {
    if (this.contributionType.hasError('contributionBlocked')) return;
    if (
      [CONTRIBUTION_TYPE.PUBLICLY_TRADED_SECURITIES, CONTRIBUTION_TYPE.OTHER_ASSETS].includes(
        contributionType
      ) &&
      !this.constData.isGlf
    ) {
      this.amount.label = 'donate.contribution.labels.est.amount';
      this.assetDescription.setValidators([CustomValidators.required]);
      this.assetDescription.visible = true;
    } else {
      this.amount.label = 'contribute.section4.amount.label';
      this.assetDescription.visible = false;
      this.assetDescription.clearValidators();
      this.assetDescription.updateValueAndValidity();
    }
  }

  setDafFields(contributionType: CONTRIBUTION_TYPE): void {
    if (contributionType === CONTRIBUTION_TYPE.DONOR_ADVISED_FUND) {
      this.dafProvider.setValidators([CustomValidators.required]);
      this.dafProvider.visible = true;
      this.fundName.setValidators([CustomValidators.required]);
      this.fundName.visible = true;
    } else {
      this.dafProvider.visible = false;
      this.dafProvider.clearValidators();
      this.dafProvider.updateValueAndValidity();
      this.fundName.visible = false;
      this.fundName.clearValidators();
      this.fundName.updateValueAndValidity();
    }
  }

  setAmountMax(): void {
    const selectedContributionType = this.contributionType.value?.code;

    this.amount.setErrors(null);
    this.amount.clearValidators();

    if (
      selectedContributionType === CONTRIBUTION_TYPE.CREDIT_CARD ||
      selectedContributionType === CONTRIBUTION_TYPE.BANK_TRANSFER_ONLINE
    ) {
      this.currency.value
        ? (this.amount.max =
            this.currency.value?.symbol === 'IDR' ? 9999999999.99 : this.defaultMax)
        : (this.amount.max = this.defaultMax);
    } else if (selectedContributionType === CONTRIBUTION_TYPE.SEPA_DEBIT) {
      this.amount.max = 10000;
    } else {
      this.amount.max = null;
    }

    this.amount.setValidators([
      CustomValidators.required,
      CustomValidators.positiveNumber,
      CustomValidators.maxValue(this.amount.max),
    ]);
    this.amount.max && this.amount.value > this.amount.max && this.amount.setValue(this.amount.max);
    this.amount.updateValueAndValidity();
  }

  setCurrency(): void {
    this.currency.enable();
    const countryData = this.countryDonatingFrom.value;
    const patchCurrency = (currency: CurrencySet | null, disable = true) => {
      this.currency.patchValue(currency);
      disable && this.currency.disable();
    };

    if (this.taxDeduction.value?.code || countryData?.name === COUNTRY_NAMES.UNITED_STATES) {
      // First try to use contriConfig.currencyUuid, fall back to mainCurrency
      const currencyUuid = countryData?.networkMember?.contriConfig?.currencyUuid;
      const currency: CurrencySet = currencyUuid
        ? countryData.currencySet.find((c: CurrencySet) => c.uuid === currencyUuid) ||
          SharedMethodsHelpers.getCurrencyBySymbol(
            countryData.currencySet,
            countryData.mainCurrency
          )
        : SharedMethodsHelpers.getCurrencyBySymbol(
            countryData.currencySet,
            countryData.mainCurrency
          );

      patchCurrency(currency);
    } else {
      this.currency.patchValue(null);
    }
  }

  setAndDisableCurrency(): void {
    this.donateAsyncService.getAllCurrencies().subscribe((response) => {
      if (response.currencyToPrefill) this.currency.setValue(response.currencyToPrefill);
      if (response.disable) this.currency.disable();
    });
  }

  getBankErrorAllowedCountries(): COUNTRY_NAMES[] {
    return [COUNTRY_NAMES.UNITED_STATES];
  }

  shouldDisplayBankErrors(): boolean {
    const currentCountry = this.countryDonatingFrom?.value?.name;
    return (
      this.constData?.isIgf ||
      (this.constData?.isSwiss && this.getBankErrorAllowedCountries().includes(currentCountry))
    );
  }

  setPurpose(allowAnonymousToOwnDAF: boolean): void {
    if (this.constData.isPublic) {
      this.contributionPurpose.setValidators([
        CustomValidators.maxLength(250),
        Validators.required,
      ]);
    }
    // Baseline validators for the currently-composed control (covers public /
    // non-public DAF-owner exemption / default). Restored on no-config country switches.
    // Stored as a raw array (not `.validator`'s composed function) so `hasValidator()` —
    // used by the template to show the required asterisk — can still detect Validators.required.
    const isExemptFromRequired =
      !this.constData.isPublic && this.isDAFOwner && allowAnonymousToOwnDAF;
    this.defaultContributionPurposeValidator = isExemptFromRequired
      ? [CustomValidators.maxLength(250)]
      : [CustomValidators.maxLength(250), Validators.required];
    this.setPurposeDefaultValue();
  }

  private setPurposeDefaultValue(): void {
    if (!this.constData.isApplicationAssessment) {
      if (this.constData.isSwiss && this.organizationName !== undefined) {
        this.translateService
          .translate('DONATE_CONTRIBUTION_FOR')
          .subscribe((purposeDefaultValue: string) => {
            this.contributionPurpose.patchValue(purposeDefaultValue + ' ' + this.organizationName);
          });
      } else {
        this.translateService
          .translate('contribute.section4.purpose.placeholder')
          .subscribe((purposeDefaultValue: string) => {
            this.contributionPurpose.patchValue(purposeDefaultValue);
          });
      }
    }
  }

  applyContributionPurposeConfig(cfg: NetworkMember['contriConfig']): void {
    // Label always resolves (default when missing/empty) so it can't go stale across countries.
    this.contributionPurpose.label = cfg?.contributionPurposeFieldLabel?.trim()
      ? cfg.contributionPurposeFieldLabel
      : 'contribute.section4.purpose.label';

    // Custom override (or null) surfaced for the read-only summary. Reset every country change.
    this.contributionPurposeConfigLabel = cfg?.contributionPurposeFieldLabel?.trim()
      ? cfg.contributionPurposeFieldLabel
      : null;

    const hasPurposeConfig =
      !!cfg &&
      (cfg.contributionPurposeRequired != null ||
        cfg.contributionPurposeFieldLabel != null ||
        cfg.contributionPurposeDefaultValue != null);

    // No config, or none of the purpose fields are set (e.g. configs created before these
    // fields existed): restore the generic baseline so neither the value nor required-ness
    // from a previously-selected country persists.
    if (!hasPurposeConfig) {
      this.contributionPurpose.setValidators(this.defaultContributionPurposeValidator);
      this.contributionPurpose.updateValueAndValidity();
      this.setPurposeDefaultValue();
      return;
    }
    // Required-ness driven by the config flag.
    const validators = [CustomValidators.maxLength(250)];
    if (cfg.contributionPurposeRequired) validators.push(Validators.required);
    this.contributionPurpose.setValidators(validators);
    this.contributionPurpose.updateValueAndValidity();
    // Default value: config value if present, else current translation/default logic.
    if (cfg.contributionPurposeDefaultValue?.trim()) {
      this.contributionPurpose.patchValue(cfg.contributionPurposeDefaultValue);
    } else {
      this.setPurposeDefaultValue();
    }
  }

  checkMinimumAmount(): void {
    const country = this.countryDonatingFrom.value;

    if (!this.amount?.value || !this.currency?.value) {
      this.amount.loading = false;
      return;
    }

    const minimumContributionValue = this.getNetworkMemberMinimumContribution(country);
    const isTaxDeductible = this.taxDeduction?.value?.code;
    const isSameCurrency = country?.mainCurrency === this.currency.value?.symbol;

    if (isTaxDeductible && minimumContributionValue != null) {
      this.handleNetworkMemberMinimum(minimumContributionValue);
    } else {
      this.handleCurrencyMinimum();
    }
  }

  private getNetworkMemberMinimumContribution(country: any): number | null | undefined {
    if (!country?.networkMember?.name) {
      return null;
    }

    // First try contriConfig.minimumContribution, fall back to config.minimumContribution
    return (
      country.networkMember.contriConfig?.minimumContribution ??
      country.networkMember.config?.minimumContribution
    );
  }

  private handleNetworkMemberMinimum(minimumContributionValue: number): void {
    const minimumContribution = parseFloat(String(minimumContributionValue));
    const existingErrors = this.amount.errors;

    this.resetAmountErrors();

    if (minimumContribution && this.amount.value < minimumContribution) {
      this.setMinimumAmountError(minimumContribution);
    } else {
      this.amount.setErrors(existingErrors);
    }

    this.amount.loading = false;
  }

  private handleCurrencyMinimum(): void {
    this.checkMinimumAmount$.next();
  }

  private handleTaxDeductionMinimum(res: CurrencyCheck, existingErrors: any): void {
    if (this.taxDeductionValue) {
      let minAmount = parseFloat(res.value);

      if (this.countryName === COUNTRY_NAMES.CANADA) {
        minAmount =
          this.contributionType.value?.code === CONTRIBUTION_TYPE.DONOR_ADVISED_FUND ? 100 : null;
      }

      if (minAmount && this.amount.value < minAmount) {
        this.setMinimumAmountError(minAmount);
      }
    } else if (existingErrors?.maxValue) {
      this.amount.setErrors(existingErrors);
    }
  }

  private resetAmountErrors(): void {
    this.amount.setErrors(null);
    this.amount.helperData.minAmount = null;
  }

  private setMinimumAmountError(minAmount: number | string): void {
    this.amount.helperData.minAmount = minAmount;
    this.amount.setErrors({ minAmount: true });
  }

  stopFlowFor(contributionType: CONTRIBUTION_TYPE): void {
    const contributionTypeCode =
      typeof this.contributionType.value === 'string'
        ? this.contributionType.value
        : this.contributionType.value.code;
    if (contributionTypeCode === contributionType) {
      this.contributionType.setErrors({ contributionBlocked: true });
    } else if (contributionTypeCode) this.contributionType.setErrors(null);
  }

  setStateProvinceValidity(): void {
    const nativeCountry = this.country.value?.name;
    if (this.stateProvince.type === INPUT_TYPE.DROPDOWN) {
      this.stateProvince.setValue(null);
    }
    this.stateProvince.type =
      nativeCountry === COUNTRY_NAMES.UNITED_STATES ? INPUT_TYPE.DROPDOWN : INPUT_TYPE.TEXT;
    const stateProvinceRequired = !![
      COUNTRY_NAMES.CANADA,
      COUNTRY_NAMES.AUSTRALIA,
      COUNTRY_NAMES.UNITED_STATES,
    ].includes(nativeCountry);
    if (stateProvinceRequired) {
      this.stateProvince.setValidators(Validators.required);
    } else {
      this.stateProvince.clearValidators();
    }
    if (this.stateProvince.type !== INPUT_TYPE.DROPDOWN) {
      this.stateProvince.setValidators([
        CustomValidators.lettersNumbersCharacters,
        CustomValidators.minLength(2),
        CustomValidators.maxLength(30),
      ]);
    } else if (stateProvinceRequired) {
      this.stateProvince.setValidators(Validators.required);
    } else {
      this.stateProvince.clearValidators();
    }
    this.stateProvince.updateValueAndValidity();
  }

  setCompanyNameValidity(): void {
    if (this.isCompany.value) {
      this.companyName.setValidators([Validators.required, CustomValidators.maxLength(74)]);
    } else {
      this.companyName.clearValidators();
    }
    this.companyName.updateValueAndValidity();
  }

  changeCountry(country: SearchCountry): void {
    this.dynamicData.displayNetworkMember = false;
    this.countryDonatingFrom.patchValue(country);
    this.handleCountryChanged(this.countryDonatingFrom.value);
    this.setTaxDeduction();
    this.setCurrency();

    // Use appropriate contribution types based on tax deduction selection
    const taxDeductibleTypes = country?.networkMember?.contriConfig?.taxDeductibleContributionTypes;
    const nonTaxDeductibleTypes =
      country?.networkMember?.contriConfig?.nonTaxDeductibleContributionTypes;

    if (this.taxDeductionValue && taxDeductibleTypes && taxDeductibleTypes.length > 0) {
      // Tax deduction selected: use taxDeductibleContributionTypes
      this.dynamicData.setContributionTypeOptionsFromApi(taxDeductibleTypes);
    } else if (
      !this.taxDeductionValue &&
      nonTaxDeductibleTypes &&
      nonTaxDeductibleTypes.length > 0 &&
      this.countryName !== COUNTRY_NAMES.UNITED_KINGDOM
    ) {
      // No tax deduction: use nonTaxDeductibleContributionTypes
      this.dynamicData.setContributionTypeOptionsFromApi(nonTaxDeductibleTypes);
    } else {
      // Fallback to current behavior (also handles UK with no tax deduction)
      this.dynamicData.setContributionTypeOptions(this.countryName, this.taxDeductionValue);
    }
  }

  selectCharity(charity: Charity): void {
    this.charity.patchValue(charity);
    this.changeDetector.detectChanges();
    // checkDonation() is called automatically by charity.valueChanges subscription
  }

  // Prefill after redirect
  PrepareFormForPrefill(tokenData: TokenData): void {
    if (tokenData.currencies) this.dynamicData.availableCurrencies = tokenData.currencies;
    this.prefillFormAfterRedirect(tokenData.data);
    this.donateForm.disable({ emitEvent: false });
  }

  prefillAndDisableCharity(charity: CustomLinkFund): void {
    this.charity.patchValue(new Charity(charity));
    this.charity.disable();
  }

  prefillFormAfterRedirect(data: RepopulateData): void {
    this.contributionTypeInfo.patchValue(
      {
        contributionType: ContributionTypes.find((type) => type.code === data.contributionType),
        dafProvider: data.dafProvider,
        fundName: data.fundName,
        assetDescription: data.assetDescription,
      },
      { emitEvent: false }
    );
    this.taxDeduction.setValue({ code: true, value: 'Yes' }, { emitEvent: false });
    this.charity.setValue(
      {
        cseg5: data.fundNumberInternalId,
        fundNumber: data.fundNumber,
        fundName: data.entityName,
        name: data.entityName,
      },
      { emitEvent: false }
    );
    this.financialInfo.patchValue(
      {
        amount: data.amount,
        anonymousDonation: data.anonymousDonation,
        currency: this.dynamicData.availableCurrencies.find(
          (curr) => curr.symbol === data.currencySymbol
        ),
        contributionPurpose: data.contributionPurpose,
      },
      { emitEvent: false }
    );
    if (data.recurrencePattern) {
      this.recurrenceForm.patchValue(
        {
          recurrencePattern: data.recurrencePattern,
          recurrenceRange: {
            startDate: data.startDate,
            endDate: data.endDate,
            rangeOccurrences: data.rangeOccurrences,
            rangeOption: data.rangeOption,
          },
        },
        { emitEvent: false }
      );
    }
    this.giverInfo.patchValue(
      {
        city: data.city,
        isCompany: data.company,
        companyName: data.companyName,
        country: this.constData.availableCountries.find((country) => country.name === data.country),
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        middleName: data.middleName,
        postalCode: data.postalCode,
        stateProvince: data.state,
        streetAddress: data.streetAddress,
      },
      { emitEvent: false }
    );

    this.shareDonorDetails.patchValue(data.shareDonorDetails, { emitEvent: false });

    const contributionTypeCode =
      typeof this.contributionType.value === 'string'
        ? this.contributionType.value
        : this.contributionType.value.code;
    this.dynamicData.setNextButtonText(
      contributionTypeCode,
      this.taxDeduction.value?.code,
      this.constData.isSwiss,
      this.constData.isCncf
    );
  }

  handleCountryChanged(country?: SearchCountry): void {
    this.taxDeduction?.patchValue(null);
    this.clearServerError();
    this.clearNetworkMemberInfo();
    const isNZRecurring =
      country?.name === COUNTRY_NAMES.NEW_ZEALAND && this.dynamicData?.isRecurring;

    if (country) {
      this.updateCountrySpecificFlags(country);
      this.configureTaxDeduction(country, isNZRecurring);
      this.configureAvailableCurrencies(country);
      this.handleUSNetworkMember(country);
      this.setTaxDeductionDetails();
    } else {
      this.resetCountryData();
    }

    this.finalizeCountryChange(isNZRecurring);
  }

  private updateCountrySpecificFlags(country: SearchCountry): void {
    this.isBrazil = country.name === COUNTRY_NAMES.BRAZIL && this.constData?.isSwiss;
    this.isSwitzerland =
      this.countryDonatingFrom?.value?.name === COUNTRY_NAMES.SWITZERLAND &&
      this.constData?.isSwiss;
  }

  private configureTaxDeduction(country: SearchCountry, isNZRecurring: boolean): void {
    // Check if networkMember has taxDeductibleContributionTypes configured
    const hasTaxDeductibleTypes =
      country?.networkMember?.contriConfig?.taxDeductibleContributionTypes?.length > 0;

    if (country.taxDeduction || hasTaxDeductibleTypes || this.constData?.isSwiss) {
      const countryValue = this.countryDonatingFrom?.value;
      if (countryValue) {
        countryValue.taxDeductionOptions =
          isNZRecurring && !this.constData?.isPublic
            ? TaxDeductionValues.filter(({ code }) => !code)
            : TaxDeductionValues;
      }
      this.taxDeduction?.setValidators([Validators.required]);
    } else {
      this.taxDeduction?.clearValidators();
      country.currencySet =
        country.currencySet?.filter((currency: CurrencySet) => currency.taxDeduction === 'NA') ||
        [];
    }
    this.taxDeduction?.updateValueAndValidity();
  }

  private configureAvailableCurrencies(country: SearchCountry): void {
    if (this.dynamicData && country.currencySet) {
      this.dynamicData.availableCurrencies = [...country.currencySet];
      if (this.dynamicData.availableCurrencies.length === 1) {
        this.currency?.patchValue(this.dynamicData.availableCurrencies[0]);
      }
    }
  }

  private handleUSNetworkMember(country: SearchCountry): void {
    if (country.name === COUNTRY_NAMES.UNITED_STATES && this.dynamicData) {
      this.dynamicData.displayNetworkMember = true;
    }
  }

  private resetCountryData(): void {
    this.taxDeduction?.patchValue(null);
    if (this.dynamicData) {
      this.dynamicData.displayTaxDeduction = false;
    }
    this.taxDeduction?.clearValidators();
    this.currency?.patchValue(null);
    if (this.charity) {
      this.charity.label = 'contribute.section2.charity.label';
    }
  }

  private finalizeCountryChange(isNZRecurring: boolean): void {
    if (isNZRecurring && !this.constData?.isPublic) {
      this.taxDeduction?.patchValue(TaxDeductionValues[1]);
    } else {
      this.taxDeduction?.patchValue('');
    }

    this.contributionType?.reset();
    this.contributionType?.setValue('');
    this.changeDetector?.detectChanges();

    if (this.constData?.isPublic && !this.charity?.disabled) {
      this.charity?.patchValue(null);
    }
    this.amount?.patchValue(null);
  }

  setTaxDeductionDetails(): void {
    const country = this.countryDonatingFrom?.value;

    if (!country) {
      return;
    }

    const isUS = country.name === COUNTRY_NAMES.UNITED_STATES;
    const minimumContribution = country.networkMember?.config?.minimumContribution;
    const hasNetworkMemberInfo = country.networkMember?.description && country.networkMember?.name;

    this.dynamicData.displayNetworkMember = hasNetworkMemberInfo;
    this.dynamicData?.setNetworkMemberInfo(this.countryName, minimumContribution);
  }

  checkDonation(
    { networkMemberInfo }: { networkMemberInfo: boolean } = { networkMemberInfo: false }
  ): void {
    const payload = new CheckContributionPayload(
      this.countryName,
      this.charityValue.fundNumber,
      this.taxDeductionValue,
      !!this.constData.customLink
    );
    this.donateAsyncService.checkContribution(payload, this.charity, networkMemberInfo).subscribe();
  }

  resetForm(): void {
    this.clearNetworkMemberInfo();
    this.dynamicData.displayTaxDeduction = false;

    // Clear contribution type - this will trigger valueChanges subscription
    // which will call hideAllContributionTypeDependentFields()
    this.contributionType.patchValue(null, { emitEvent: true });

    // Reset country (this also triggers its own cleanup via valueChanges)
    this.countryDonatingFrom.patchValue(null);
    this.handleCountryChanged();

    // Mark form as untouched
    this.donateForm.markAsUntouched();

    // Explicitly reset dynamic data states (defensive programming)
    if (this.dynamicData) {
      this.dynamicData.displayBankDetails = false;
      // Reset recurring states if needed
      if (this.constData.isPublic) {
        this.dynamicData.isPublicRecurring = false;
      } else {
        this.dynamicData.isRecurring = false;
      }
    }
  }

  private clearServerError(): void {
    if (this.dynamicData.serverError) {
      this.dynamicData.serverError = null;
    }
  }

  private clearNetworkMemberInfo(): void {
    if (this.dynamicData?.networkMemberInfo !== null) {
      this.dynamicData.networkMemberInfo = null;
      this.dynamicData.displayNetworkMember = false;
    }
    this.dynamicData.warningMessage = null;
  }
  // Getters
  get loggedInFundType(): string | undefined {
    return this.fundService.selectedFund()?.fundType;
  }

  get countryDonatingFrom(): CustomControl {
    return this.donateForm
      ?.get('countryAndCharityInfo')
      .get('countryDonatingFrom') as CustomControl;
  }
  get countryName(): COUNTRY_NAMES {
    return this.countryDonatingFrom.value?.name;
  }
  get networkMember(): NetworkMember {
    return this.countryDonatingFrom.value?.networkMember;
  }
  get taxDeduction(): CustomControl {
    return this.donateForm.get('countryAndCharityInfo').get('taxDeduction') as CustomControl;
  }

  get specificCharity(): CustomControl {
    return this.donateForm.get('countryAndCharityInfo').get('specificCharity') as CustomControl;
  }
  get taxDeductionValue(): boolean {
    return this.taxDeduction.value?.code || false;
  }
  get contributionTypeInfo(): UntypedFormGroup {
    return this.donateForm.get('contributionTypeInfo') as UntypedFormGroup;
  }

  get contributionType(): CustomControl {
    return this.donateForm?.get('contributionTypeInfo').get('contributionType') as CustomControl;
  }

  get assetDescription(): CustomControl {
    return this.donateForm.get('contributionTypeInfo').get('assetDescription') as CustomControl;
  }

  get dafProvider(): CustomControl {
    return this.donateForm.get('contributionTypeInfo').get('dafProvider') as CustomControl;
  }

  get fundName(): CustomControl {
    return this.donateForm.get('contributionTypeInfo').get('fundName') as CustomControl;
  }

  get financialInfo(): UntypedFormGroup {
    return this.donateForm.get('financialInfo') as UntypedFormGroup;
  }

  get currency(): CustomControl {
    return this.donateForm.get('financialInfo').get('currency') as CustomControl;
  }

  get amountCurrency(): CustomControl {
    return this.donateForm.get('financialInfo').get('amountCurrency') as CustomControl;
  }

  get amount(): CustomControl {
    return this.donateForm.get('financialInfo').get('amount') as CustomControl;
  }

  get contributionPurpose(): CustomControl {
    return this.donateForm.get('financialInfo').get('contributionPurpose') as CustomControl;
  }

  get anonymousDonation(): CustomControl {
    return this.donateForm.get('financialInfo').get('anonymousDonation') as CustomControl;
  }

  get recurrenceForm(): CustomControl {
    return this.donateForm.get('financialInfo').get('recurrenceForm') as CustomControl;
  }

  get charity(): CustomControl {
    return this.donateForm.get('countryAndCharityInfo').get('charity') as CustomControl;
  }

  get charityValue(): Fund | SearchCharity {
    if (this.constData.isPublic) return this.charity.value;
    return this.charity.value || this.fundService.selectedFund();
  }

  get giverInfo(): UntypedFormGroup {
    return this.donateForm.get('giverInfo') as UntypedFormGroup;
  }

  get isCompany(): CustomControl {
    return this.donateForm.get('giverInfo').get('isCompany') as CustomControl;
  }

  get companyName(): CustomControl {
    return this.donateForm.get('giverInfo').get('companyName') as CustomControl;
  }

  get firstName(): CustomControl {
    return this.donateForm.get('giverInfo').get('firstName') as CustomControl;
  }

  get lastName(): CustomControl {
    return this.donateForm.get('giverInfo').get('lastName') as CustomControl;
  }

  get middleName(): CustomControl {
    return this.donateForm.get('giverInfo').get('middleName') as CustomControl;
  }

  get email(): CustomControl {
    return this.donateForm.get('giverInfo').get('email') as CustomControl;
  }

  get streetAddress(): CustomControl {
    return this.donateForm.get('giverInfo').get('streetAddress') as CustomControl;
  }

  get country(): CustomControl {
    return this.donateForm.get('giverInfo').get('country') as CustomControl;
  }

  get city(): CustomControl {
    return this.donateForm.get('giverInfo').get('city') as CustomControl;
  }

  get stateProvince(): CustomControl {
    return this.donateForm.get('giverInfo').get('stateProvince') as CustomControl;
  }

  get postalCode(): CustomControl {
    return this.donateForm.get('giverInfo').get('postalCode') as CustomControl;
  }

  get countryAndCharityInfo(): CustomControl {
    return this.donateForm.get('countryAndCharityInfo') as CustomControl;
  }

  get shareDonorDetails(): CustomControl {
    return this.donateForm.get('giverInfo').get('shareDonorDetails') as CustomControl;
  }

  get privacyConsent(): CustomControl {
    return this.donateForm.get('giverInfo').get('privacyConsent') as CustomControl;
  }

  get isSwitzerlandWithCode(): boolean {
    return this.isSwitzerland && this.taxDeductionValue;
  }

  get taxDeductionFund(): CustomControl {
    return this.donateForm.get('countryAndCharityInfo').get('taxDeductionFund') as CustomControl;
  }

  get shouldShowOriginalDonorRecommendation(): boolean {
    return (
      (this.constData.isPublic && !this.constData.customLink) ||
      (!this.constData.isPublic && this.loggedInFundType !== FundTypes.DAF) ||
      (this.constData.customLink && this.charityValue.fundType !== FundTypes.DAF)
    );
  }
}
